import { useEffect, useRef } from "react";
import { identifySpokenPlayer } from "../../utils/tableSetupVoiceCommands";
import { saveCaptureSegment } from "../../components/funtionsBD/Mainservice";
import { createContinuousSpeechSession } from "../../voice/ContinuousSpeechSession";

/**
 * Listens for the whole captured session and stores each phrase on the
 * current block. Player-addressed lines become feedback; everything else
 * stays on the block for the session summary.
 */
export function LiveTrainingCaptureListener({
  enabled = false,
  paused = false,
  tsId = null,
  teId = null,
  phase = "before",
  sequenceOrder = null,
  players = [],
  language = "en",
  onState = null,
}) {
  const ctxRef = useRef({});
  const sessionRef = useRef(null);
  ctxRef.current = { tsId, teId, phase, sequenceOrder, players, paused };

  useEffect(() => {
    if (!enabled || !tsId) return undefined;
    let cancelled = false;

    const onUtterance = async (transcript) => {
      if (cancelled) return;
      const ctx = ctxRef.current;
      if (!ctx.tsId || !ctx.teId || ctx.paused) return;
      const identified = identifySpokenPlayer(transcript, ctx.players || []);
      const player = identified.confident ? identified.player : null;
      try {
        await saveCaptureSegment({
          ts_id: ctx.tsId,
          te_id: ctx.teId,
          phase: ctx.phase || "before",
          sequence_order: ctx.sequenceOrder,
          transcript,
          receiver_user_id: player?.user_id || null,
          player_id: player?.player_id || null,
        });
      } catch {
        /* keep listening; the next phrase can still be stored */
      }
    };

    const session = createContinuousSpeechSession({
      languageHint: language,
      onUtterance,
      onState,
    });
    sessionRef.current = session;
    session.start();
    if (paused) session.pause();

    return () => {
      cancelled = true;
      session.stop();
      if (sessionRef.current === session) sessionRef.current = null;
    };
  }, [enabled, tsId, language, onState]);

  useEffect(() => {
    const session = sessionRef.current;
    if (!session || !enabled) return;
    if (paused) session.pause();
    else session.resume();
  }, [paused, enabled]);

  return null;
}

export default LiveTrainingCaptureListener;
